import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Chip,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Button,
  Alert,
  InputAdornment,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  arePriceRangesEditable,
  describeOperationTarget,
  getOperationAmount,
  getOperationConflictKey,
  getOperationPriceRanges,
  isAmountEditable,
  isAmountMissing,
  normalizeOperationNode,
} from '../utils/operationIdentity';
import { useStoreOperationEdits } from '../hooks/useStoreOperationEdits';
import { formatCents, parseCentsInput } from '../utils/namedPriceUtils';
import {
  RangeItem,
  rangesFromWeightPriceRanges,
  summarizeWeightPriceRanges,
  validateRanges,
  weightPriceRangesFromRanges,
} from '../utils/priceRanges';
import { WeightPriceRanges } from './selectors/WeightPriceRanges';
import { formatOperationTypeName } from '../constants/operationTypes';

interface StoreOperationsDialogProps {
  open: boolean;
  onClose: () => void;
  patchId: string;
  patchName: string;
  storeNumber: string;
  /**
   * The already-loaded patch. This dialog never fetches: patch payloads are large and loading stays
   * user-initiated from the patch list, so it renders whatever the list has cached.
   */
  patchData: any;
}

interface OperationRow {
  node: any;
  /** Stable within a patch, and a good React key. */
  patchOperationId: string;
  conflictKey: string;
  typeName: string;
  typeLabel: string;
  description: string;
  /** The amount on the patch today, before any pending edit. */
  currentAmount?: number;
  editable: boolean;
  supported: boolean;
  /** Priced by nature, but the payload did not include the value - see isAmountMissing. */
  amountMissing: boolean;
  /** The ranges on the patch today, for the one type that holds several prices instead of one. */
  currentPriceRanges?: any[];
  /** True for `setModifierPriceRanges`, whose prices are edited in an expanded row rather than a cell. */
  rangesEditable: boolean;
}

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

/** Type, Target, Amount, Status, Actions - drives the colSpan of an expanded price-ranges row. */
const TABLE_COLUMN_COUNT = 5;

/**
 * A store's patch operations, in full, with direct editing and deletion.
 *
 * A patch can carry thousands of operations, which is why this is a full-page surface with search
 * rather than anything inline. Edits are written straight into Redux as store-scoped pending
 * operations, so closing and reopening shows the same work, and clearing pending changes clears it -
 * there is no separate draft state to keep in sync.
 */
export const StoreOperationsDialog: React.FC<StoreOperationsDialogProps> = ({
  open,
  onClose,
  patchId,
  patchName,
  storeNumber,
  patchData,
}) => {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  /** Amount fields being typed in, keyed by patchOperationId. Committed on blur or Enter. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  /**
   * Price-range editors currently open, keyed by patchOperationId, each holding its own rows.
   *
   * Presence is what "expanded" means, so closing an editor and discarding its draft are the same act.
   * Keyed rather than single so opening a second editor cannot silently throw away the first's edits.
   */
  const [rangeDrafts, setRangeDrafts] = useState<Record<string, RangeItem[]>>({});

  const {
    pendingCount,
    stateFor,
    displayedAmountFor,
    displayedPriceRangesFor,
    setAmount,
    setPriceRanges,
    removeOperation,
    undo,
  } = useStoreOperationEdits({ patchId, storeNumber });

  const rows = useMemo<OperationRow[]>(() => {
    const edges = patchData?.patchOperations?.edges;
    if (!Array.isArray(edges)) return [];

    return edges
      .map((edge: any, index: number) => {
        const node = edge?.node;
        if (!node) return null;

        const normalized = normalizeOperationNode(node);

        return {
          node,
          // Fall back to the index so a payload without operation ids still renders stable rows.
          patchOperationId: node.patchOperationId || `row-${index}`,
          conflictKey: getOperationConflictKey(node),
          typeName: node.__typename || 'Unknown',
          typeLabel: formatOperationTypeName(node.__typename || 'Unknown'),
          description: describeOperationTarget(node),
          currentAmount: getOperationAmount(node),
          editable: isAmountEditable(node),
          supported: normalized !== null,
          amountMissing: isAmountMissing(node),
          currentPriceRanges: getOperationPriceRanges(node),
          rangesEditable: arePriceRangesEditable(node),
        };
      })
      .filter(Boolean) as OperationRow[];
  }, [patchData]);

  const operationTypes = useMemo(
    () => Array.from(new Set(rows.map((row) => row.typeName))).sort(),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      if (typeFilter !== 'all' && row.typeName !== typeFilter) return false;
      if (!term) return true;
      // Ids are searchable too - they are often all a payload gives us to go on.
      return (
        row.description.toLowerCase().includes(term) ||
        row.typeLabel.toLowerCase().includes(term) ||
        row.conflictKey.toLowerCase().includes(term)
      );
    });
  }, [rows, search, typeFilter]);

  // A narrower filter can leave the current page past the end of the results.
  useEffect(() => {
    setPage(0);
  }, [search, typeFilter, rowsPerPage]);

  const visibleRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const rowStateOf = useCallback((row: OperationRow) => stateFor(row.conflictKey), [stateFor]);

  const commitAmount = useCallback(
    (row: OperationRow, raw: string) => {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[row.patchOperationId];
        return next;
      });

      const amount = parseCentsInput(raw);
      // An unparseable or unchanged value is simply discarded - the field reverts on its own.
      if (amount === null || amount === displayedAmountFor(row.node, row.currentAmount)) return;

      setAmount(row.node, amount, row.currentAmount);
    },
    [displayedAmountFor, setAmount]
  );

  const undoRow = useCallback(
    (row: OperationRow) => {
      undo(row.conflictKey);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[row.patchOperationId];
        return next;
      });
      setRangeDrafts((prev) => {
        const next = { ...prev };
        delete next[row.patchOperationId];
        return next;
      });
    },
    [undo]
  );

  const closeRangeEditor = useCallback((row: OperationRow) => {
    setRangeDrafts((prev) => {
      const next = { ...prev };
      delete next[row.patchOperationId];
      return next;
    });
  }, []);

  /** Opens the editor seeded from what the row currently shows, or closes it, discarding the draft. */
  const toggleRangeEditor = useCallback(
    (row: OperationRow) => {
      setRangeDrafts((prev) => {
        if (prev[row.patchOperationId]) {
          const next = { ...prev };
          delete next[row.patchOperationId];
          return next;
        }

        // Seeded from the displayed ranges, so reopening after an edit continues from that edit rather
        // than from the patch's original values.
        return {
          ...prev,
          [row.patchOperationId]: rangesFromWeightPriceRanges(
            displayedPriceRangesFor(row.node, row.currentPriceRanges)
          ),
        };
      });
    },
    [displayedPriceRangesFor]
  );

  const applyRangeEdits = useCallback(
    (row: OperationRow) => {
      const rows = rangeDrafts[row.patchOperationId];
      // Open-ended last ranges are permitted here: real patches contain them, and demanding an end
      // value would silently close one the user never touched.
      if (!rows || !validateRanges(rows, { allowOpenEndedLast: true })) return;

      setPriceRanges(row.node, weightPriceRangesFromRanges(rows));
      closeRangeEditor(row);
    },
    [closeRangeEditor, rangeDrafts, setPriceRanges]
  );

  return (
    <Dialog fullScreen open={open} onClose={onClose} data-testid='storeOperationsDialog'>
      <AppBar position='sticky' color='default' elevation={1}>
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant='h6' component='div' noWrap>
              Operations — Store {storeNumber}
            </Typography>
            <Typography variant='body2' color='textSecondary' noWrap>
              {patchName} · {patchId}
            </Typography>
          </Box>
          {pendingCount > 0 && (
            <Chip
              size='small'
              color='primary'
              label={`${pendingCount} pending`}
              data-testid='storeOperationsPendingCount'
            />
          )}
          <IconButton onClick={onClose} data-testid='closeStoreOperationsDialog' aria-label='Close operations'>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        <Alert severity='info' sx={{ mb: 2 }}>
          Changes here apply to store {storeNumber} only. Where they target the same thing as an
          all-store operation, this store keeps its own value and the all-store operation is skipped for
          it. Amounts are in whole cents.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            size='small'
            label='Search operations'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: 280 }}
            inputProps={{ 'data-testid': 'storeOperationsSearch' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size='small'
            label='Operation type'
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            sx={{ minWidth: 240 }}
            data-testid='storeOperationsTypeFilter'
          >
            <MenuItem value='all'>All types ({rows.length})</MenuItem>
            {operationTypes.map((typeName) => (
              <MenuItem key={typeName} value={typeName}>
                {formatOperationTypeName(typeName)}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant='body2' color='textSecondary'>
              {filteredRows.length.toLocaleString()} of {rows.length.toLocaleString()} operations
            </Typography>
          </Box>
        </Box>

        {rows.length === 0 ? (
          <Alert severity='warning'>This patch has no operations to show.</Alert>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Target</TableCell>
                    <TableCell align='right'>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => {
                    const state = rowStateOf(row);
                    const displayed = displayedAmountFor(row.node, row.currentAmount);
                    const draft = drafts[row.patchOperationId];
                    const isDeleted = state === 'deleted';
                    const isEdited = state === 'edited';
                    // Nothing queued and nothing to warn about. Most rows on a real patch look like
                    // this, and an empty cell reads as a broken column rather than a quiet one.
                    const isUnchanged = state === 'clean' && !row.amountMissing && row.supported;

                    const rangeDraft = rangeDrafts[row.patchOperationId];
                    const displayedRanges = row.rangesEditable
                      ? displayedPriceRangesFor(row.node, row.currentPriceRanges)
                      : undefined;
                    const rangeSummary = summarizeWeightPriceRanges(displayedRanges);
                    const rangeDraftValid = rangeDraft
                      ? validateRanges(rangeDraft, { allowOpenEndedLast: true })
                      : false;

                    return (
                      <React.Fragment key={row.patchOperationId}>
                      <TableRow
                        hover
                        sx={{ opacity: isDeleted ? 0.6 : 1 }}
                        data-testid={`operationRow-${row.patchOperationId}`}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.typeLabel}</TableCell>
                        <TableCell
                          sx={{ textDecoration: isDeleted ? 'line-through' : undefined }}
                        >
                          {row.description}
                        </TableCell>
                        <TableCell align='right'>
                          {row.rangesEditable ? (
                            /* Several prices, not one - the editor needs its own row below. */
                            <Button
                              size='small'
                              disabled={isDeleted}
                              onClick={() => toggleRangeEditor(row)}
                              endIcon={
                                <KeyboardArrowDownIcon
                                  fontSize='small'
                                  sx={{
                                    transition: 'transform 150ms',
                                    transform: rangeDraft ? 'rotate(180deg)' : 'none',
                                  }}
                                />
                              }
                              aria-expanded={Boolean(rangeDraft)}
                              aria-label={`${rangeDraft ? 'Hide' : 'Edit'} price ranges for ${row.description}`}
                              data-testid={`priceRangesToggle-${row.patchOperationId}`}
                              sx={{ textTransform: 'none', fontVariantNumeric: 'tabular-nums' }}
                            >
                              {rangeSummary || 'No ranges'}
                            </Button>
                          ) : row.editable && !isDeleted ? (
                            <TextField
                              size='small'
                              placeholder={row.amountMissing ? 'Not retrieved' : undefined}
                              value={draft ?? (displayed === undefined ? '' : String(displayed))}
                              onChange={(event) =>
                                setDrafts((prev) => ({ ...prev, [row.patchOperationId]: event.target.value }))
                              }
                              onBlur={(event) => commitAmount(row, event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                              }}
                              inputProps={{
                                inputMode: 'numeric',
                                style: { textAlign: 'right', width: 90 },
                                'aria-label': `Amount in cents for ${row.description}`,
                              }}
                            />
                          ) : (
                            <Typography
                              variant='body2'
                              sx={{
                                fontVariantNumeric: 'tabular-nums',
                                textDecoration: isDeleted ? 'line-through' : undefined,
                              }}
                            >
                              {displayed === undefined ? '—' : formatCents(displayed)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEdited && (
                            <Tooltip
                              title={
                                row.currentAmount === undefined
                                  ? 'Edited for this store'
                                  : `Was ${formatCents(row.currentAmount)}`
                              }
                            >
                              <Chip size='small' color='primary' variant='outlined' label='Edited' />
                            </Tooltip>
                          )}
                          {isDeleted && <Chip size='small' color='error' variant='outlined' label='Deleted' />}
                          {state === 'clean' && row.amountMissing && (
                            <Tooltip title="This patch response does not include the current price, so there is nothing to compare against. Entering a value still sets it.">
                              <Chip size='small' color='warning' variant='outlined' label='Price not in payload' />
                            </Tooltip>
                          )}
                          {state === 'clean' && !row.supported && (
                            <Tooltip title='This operation type cannot be edited or removed here'>
                              <Chip size='small' variant='outlined' label='Read only' />
                            </Tooltip>
                          )}
                          {isUnchanged && (
                            <Typography variant='body2' color='textSecondary'>
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                          {/* Delete stays available on an edited row: deciding to drop something you
                              have just repriced should not need an Undo first. */}
                          {!isDeleted && (
                            <Tooltip
                              title={row.supported ? 'Remove this operation from this store' : 'Not supported'}
                            >
                              <span>
                                <IconButton
                                  size='small'
                                  color='error'
                                  disabled={!row.supported}
                                  onClick={() => removeOperation(row.node, row.currentAmount)}
                                  aria-label={`Remove ${row.description}`}
                                >
                                  <DeleteOutlineIcon fontSize='small' />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {state !== 'clean' && (
                            <Button
                              size='small'
                              startIcon={<UndoIcon fontSize='small' />}
                              onClick={() => undoRow(row)}
                            >
                              Undo
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* The ranges editor, mirroring the operation drawer's own controls rather than
                          offering a reduced version of them. */}
                      {rangeDraft && (
                        <TableRow data-testid={`priceRangesEditor-${row.patchOperationId}`}>
                          <TableCell colSpan={TABLE_COLUMN_COUNT} sx={{ py: 0 }}>
                            <Collapse in timeout='auto' unmountOnExit>
                              <Box sx={{ py: 2 }}>
                                <WeightPriceRanges
                                  ranges={rangeDraft}
                                  setRanges={(next) =>
                                    setRangeDrafts((prev) => ({ ...prev, [row.patchOperationId]: next }))
                                  }
                                />
                                {!rangeDraftValid && (
                                  <Alert severity='warning' sx={{ mt: 1 }}>
                                    Each range needs an end above its start and a whole-cent price. Only the
                                    last range may be left open-ended.
                                  </Alert>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                  <Button
                                    size='small'
                                    variant='contained'
                                    disabled={!rangeDraftValid}
                                    onClick={() => applyRangeEdits(row)}
                                    data-testid={`applyPriceRanges-${row.patchOperationId}`}
                                  >
                                    Apply
                                  </Button>
                                  <Button
                                    size='small'
                                    onClick={() => closeRangeEditor(row)}
                                    data-testid={`cancelPriceRanges-${row.patchOperationId}`}
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component='div'
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
            />
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default StoreOperationsDialog;
